'use client';

import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import { db } from '@/lib/firebase';
import {
  normalizeProjectFavoriteIds,
  rebaseProjectFavoritesFallback,
  type ProjectFavoritesFallback,
  shouldRetryProjectFavoritesFallback,
} from './project-favorites';

const PROJECT_FAVORITES_STORAGE_KEY_V1 = 'attune:project-favorites:v1';
const PROJECT_FAVORITES_STORAGE_KEY_V2 = 'attune:project-favorites:v2';
const PROJECT_FAVORITES_CHANGE_EVENT = 'attune:project-favorites-change';

function fallbackStorageKey(storageKey: string, userId: string) {
  return `${storageKey}:${userId}`;
}

function readFallbackFavorites(userId: string): ProjectFavoritesFallback | null {
  try {
    const currentValue = window.localStorage.getItem(
      fallbackStorageKey(PROJECT_FAVORITES_STORAGE_KEY_V2, userId)
    );
    const legacyValue = window.localStorage.getItem(
      fallbackStorageKey(PROJECT_FAVORITES_STORAGE_KEY_V1, userId)
    );
    const value = currentValue ?? legacyValue;
    if (value === null) return null;
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return {
        favoriteProjectIds: normalizeProjectFavoriteIds(parsed),
        baseFavoriteProjectIds: null,
        updatedAtMs: 0,
      };
    }
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as Record<string, unknown>;
    return {
      favoriteProjectIds: normalizeProjectFavoriteIds(record.favoriteProjectIds),
      baseFavoriteProjectIds: Array.isArray(record.baseFavoriteProjectIds)
        ? normalizeProjectFavoriteIds(record.baseFavoriteProjectIds)
        : null,
      updatedAtMs:
        typeof record.updatedAtMs === 'number' && Number.isFinite(record.updatedAtMs)
          ? record.updatedAtMs
          : 0,
    };
  } catch {
    return null;
  }
}

function writeFallbackFavorites(userId: string, fallback: ProjectFavoritesFallback) {
  try {
    window.localStorage.setItem(
      fallbackStorageKey(PROJECT_FAVORITES_STORAGE_KEY_V2, userId),
      JSON.stringify(fallback)
    );
  } catch {
    // Firestore remains the primary persistence layer when local storage is unavailable.
  }
}

function removeFallbackFavorites(userId: string) {
  try {
    window.localStorage.removeItem(fallbackStorageKey(PROJECT_FAVORITES_STORAGE_KEY_V2, userId));
    window.localStorage.removeItem(fallbackStorageKey(PROJECT_FAVORITES_STORAGE_KEY_V1, userId));
  } catch {
    // Non-blocking: stale fallback data is normalized on the next read.
  }
}

function favoriteIdsMatch(left: readonly string[], right: readonly string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

async function updateUserProjectFavorites(userId: string, favoriteProjectIds: string[]) {
  await updateDoc(doc(db, 'users', userId), {
    'preferences.favorite_project_ids': favoriteProjectIds,
    'preferences.favorite_project_ids_updated_at': serverTimestamp(),
    updated_at: serverTimestamp(),
  });
}

function timestampMillis(value: unknown): number {
  if (!value || typeof value !== 'object') return 0;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis !== 'function') return 0;
  try {
    const millis = toMillis.call(value);
    return typeof millis === 'number' && Number.isFinite(millis) ? millis : 0;
  } catch {
    return 0;
  }
}

export function useProjectFavorites() {
  const { user, userDocument } = useAuth();
  const [favoriteProjectIds, setFavoriteProjectIds] = useState<string[]>([]);
  const favoriteProjectIdsRef = useRef(favoriteProjectIds);
  const confirmedFavoriteProjectIdsRef = useRef<string[] | null>(null);
  const initializedUserIdRef = useRef<string | null>(null);
  const persistenceChainRef = useRef<Promise<void>>(Promise.resolve());

  const applyFavorites = useCallback((nextFavorites: string[]) => {
    favoriteProjectIdsRef.current = nextFavorites;
    setFavoriteProjectIds(nextFavorites);
  }, []);

  const persistFavorites = useCallback(
    (userId: string, nextFavorites: string[], showFailureToast: boolean) => {
      const write = persistenceChainRef.current
        .catch(() => undefined)
        .then(() => updateUserProjectFavorites(userId, nextFavorites));
      persistenceChainRef.current = write;

      void write
        .then(() => {
          if (initializedUserIdRef.current === userId) {
            confirmedFavoriteProjectIdsRef.current = nextFavorites;
          }
          const pendingFallback = readFallbackFavorites(userId);
          if (!pendingFallback) return;
          if (favoriteIdsMatch(pendingFallback.favoriteProjectIds, nextFavorites)) {
            removeFallbackFavorites(userId);
            return;
          }
          writeFallbackFavorites(
            userId,
            rebaseProjectFavoritesFallback(pendingFallback, nextFavorites)
          );
        })
        .catch(() => {
          if (
            showFailureToast &&
            initializedUserIdRef.current === userId &&
            favoriteIdsMatch(favoriteProjectIdsRef.current, nextFavorites)
          ) {
            toast.error('Project favorites could not sync to your profile');
          }
        });
    },
    []
  );

  useEffect(() => {
    const syncFavorites = (event: Event) => {
      applyFavorites(normalizeProjectFavoriteIds((event as CustomEvent<unknown>).detail));
    };

    window.addEventListener(PROJECT_FAVORITES_CHANGE_EVENT, syncFavorites);
    return () => window.removeEventListener(PROJECT_FAVORITES_CHANGE_EVENT, syncFavorites);
  }, [applyFavorites]);

  useEffect(() => {
    if (!user) {
      initializedUserIdRef.current = null;
      confirmedFavoriteProjectIdsRef.current = null;
      applyFavorites([]);
      return;
    }
    if (!userDocument || (userDocument.uid && userDocument.uid !== user.uid)) return;

    const fallback = readFallbackFavorites(user.uid);
    if (fallback && initializedUserIdRef.current === user.uid) return;
    const profileFavoritesValue = userDocument.preferences?.favorite_project_ids;
    const hasProfileFavorites = Array.isArray(profileFavoritesValue);
    const profileFavorites = normalizeProjectFavoriteIds(profileFavoritesValue);
    const serverUpdatedAtMs = timestampMillis(
      userDocument.preferences?.favorite_project_ids_updated_at
    );
    confirmedFavoriteProjectIdsRef.current = profileFavorites;
    if (
      fallback &&
      shouldRetryProjectFavoritesFallback(
        fallback,
        profileFavorites,
        serverUpdatedAtMs,
        hasProfileFavorites
      )
    ) {
      initializedUserIdRef.current = user.uid;
      applyFavorites(fallback.favoriteProjectIds);
      persistFavorites(user.uid, fallback.favoriteProjectIds, false);
      return;
    }
    if (fallback) removeFallbackFavorites(user.uid);

    if (hasProfileFavorites) {
      applyFavorites(profileFavorites);
      initializedUserIdRef.current = user.uid;
      return;
    }

    if (initializedUserIdRef.current === user.uid) return;
    initializedUserIdRef.current = user.uid;

    applyFavorites([]);
  }, [applyFavorites, persistFavorites, user, userDocument]);

  const saveFavorites = useCallback(
    (nextFavorites: string[]) => {
      const normalizedFavorites = normalizeProjectFavoriteIds(nextFavorites);
      applyFavorites(normalizedFavorites);
      window.dispatchEvent(
        new CustomEvent(PROJECT_FAVORITES_CHANGE_EVENT, { detail: normalizedFavorites })
      );

      if (!user) return;
      const userId = user.uid;
      const confirmedFavorites =
        confirmedFavoriteProjectIdsRef.current ??
        normalizeProjectFavoriteIds(userDocument?.preferences?.favorite_project_ids);
      const fallback: ProjectFavoritesFallback = {
        favoriteProjectIds: normalizedFavorites,
        baseFavoriteProjectIds: confirmedFavorites,
        updatedAtMs: Date.now(),
      };
      writeFallbackFavorites(userId, fallback);
      persistFavorites(userId, normalizedFavorites, true);
    },
    [applyFavorites, persistFavorites, user, userDocument]
  );

  const toggleProjectFavorite = useCallback(
    (projectId: string) => {
      const normalizedProjectId = projectId.trim();
      if (!normalizedProjectId) return;

      const currentFavorites = favoriteProjectIdsRef.current;
      saveFavorites(
        currentFavorites.includes(normalizedProjectId)
          ? currentFavorites.filter(favoriteId => favoriteId !== normalizedProjectId)
          : [...currentFavorites, normalizedProjectId]
      );
    },
    [saveFavorites]
  );

  return { favoriteProjectIds, toggleProjectFavorite };
}
